import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CollectionPage from '~/components/module/setup/CollectionPage';
import { YesNo, joinParts, str } from '~/components/module/setup/cells';
import {
  NO_SHOW_RECURRENCE_TYPES,
  createGeofenceAdapter,
  getDefaultNoShowDate,
  getTimezoneAbbr,
} from '~/apis/geofence';
import { mapboxAccessToken } from '~/utils/constants/env';
import { useSeniorTimezone } from '~/utils/hooks/useSeniorTimezone';
import { FormValues, IFormField } from '~/utils/interface/setup';

// Same fields, same order as the app: name, radius, location (picked on the map), monitor, alerts,
// and "Alert if not arrived by...". Without a Mapbox token there is no map, so lat/lng/address are typed.
const hasMap = !!mapboxAccessToken;
const noShowOn = (values: FormValues) => values.noShowEnabled === true;
const isRecurrence = (type: string) => (values: FormValues) => noShowOn(values) && values.noShowRecurrenceType === type;

const NoShowSummary = ({ form }: { form: FormValues }) => {
  const { t } = useTranslation();
  if (form.noShowEnabled !== true) return <>{t('pages.setup.table.no')}</>;

  const time = str(form.noShowTime);
  switch (form.noShowRecurrenceType) {
    case 'ONCE':
      return <>{t('pages.setup.geofences.noShowSummary.ONCE', { date: str(form.noShowDate), time })}</>;
    case 'WEEKLY':
      return <>{t('pages.setup.geofences.noShowSummary.WEEKLY', { time })}</>;
    default:
      return <>{t('pages.setup.geofences.noShowSummary.DAILY', { time })}</>;
  }
};

const GeofencesTable = ({ timezone }: { timezone: string }) => {
  const { t } = useTranslation();
  const adapter = useMemo(() => createGeofenceAdapter(timezone), [timezone]);

  const fields = useMemo<IFormField[]>(
    () => [
      { name: 'name', labelKey: 'pages.setup.geofences.name', type: 'text', required: true },
      { name: 'radiusMeters', labelKey: 'pages.setup.geofences.radius', type: 'range', min: 20, max: 150, step: 5 },
      { name: 'location', labelKey: 'pages.setup.geofences.location', type: 'map', required: true, showWhen: () => hasMap },
      { name: 'address', labelKey: 'pages.setup.fields.address', type: 'text', showWhen: () => !hasMap },
      { name: 'lat', labelKey: 'pages.setup.fields.lat', type: 'number', required: true, showWhen: () => !hasMap },
      { name: 'lng', labelKey: 'pages.setup.fields.lng', type: 'number', required: true, showWhen: () => !hasMap },
      { name: 'isActive', labelKey: 'pages.setup.geofences.isActive', type: 'switch' },
      { name: 'alertOnEnter', labelKey: 'pages.setup.geofences.alertOnEnter', type: 'checkbox', indent: true },
      { name: 'alertOnExit', labelKey: 'pages.setup.geofences.alertOnExit', type: 'checkbox', indent: true },
      { name: 'noShowEnabled', labelKey: 'pages.setup.geofences.noShowEnabled', type: 'checkbox', indent: true },
      {
        name: 'noShowRecurrenceType',
        labelKey: 'pages.setup.geofences.noShowRecurrence',
        type: 'select',
        required: true,
        indent: true,
        showWhen: noShowOn,
        options: NO_SHOW_RECURRENCE_TYPES.map((value) => ({ value, labelKey: `pages.setup.repeat.${value}` })),
      },
      {
        // the app reads this day list with Sunday = 0
        name: 'noShowRepeatWeekOn',
        labelKey: 'pages.setup.geofences.noShowRepeatOn',
        type: 'weekdays',
        weekStart: 0,
        required: true,
        indent: true,
        showWhen: isRecurrence('WEEKLY'),
      },
      {
        name: 'noShowDate',
        labelKey: 'pages.setup.geofences.noShowDate',
        type: 'date',
        required: true,
        indent: true,
        showWhen: isRecurrence('ONCE'),
      },
      {
        name: 'noShowTime',
        labelKey: 'pages.setup.geofences.noShowTime',
        // the senior's zone, shown when it differs from this computer's (as the app does)
        label:
          timezone === Intl.DateTimeFormat().resolvedOptions().timeZone
            ? t('pages.setup.geofences.noShowTime')
            : `${t('pages.setup.geofences.noShowTime')} (${getTimezoneAbbr(timezone)})`,
        type: 'time',
        required: true,
        indent: true,
        showWhen: noShowOn,
      },
    ],
    [timezone, t]
  );

  return (
    <CollectionPage
      i18nPrefix='pages.setup.geofences'
      adapter={adapter}
      fields={fields}
      defaultForm={{
        radiusMeters: 75,
        alertOnEnter: true,
        alertOnExit: true,
        isActive: true,
        noShowEnabled: false,
        noShowRecurrenceType: 'DAILY',
        noShowDate: getDefaultNoShowDate(timezone),
        noShowTime: '',
        noShowRepeatWeekOn: '',
      }}
      getTitle={(form) => str(form.name)}
      columns={[
        { key: 'name', labelKey: 'pages.setup.geofences.name', render: (form) => <span className='font-medium text-foreground'>{str(form.name)}</span> },
        {
          key: 'address',
          labelKey: 'pages.setup.fields.address',
          render: (form) => str(form.address) || joinParts([form.lat, form.lng]),
        },
        { key: 'radius', labelKey: 'pages.setup.geofences.radiusShort', render: (form) => (form.radiusMeters ? `${str(form.radiusMeters)} m` : '') },
        { key: 'active', labelKey: 'pages.setup.geofences.isActive', render: (form) => <YesNo value={form.isActive} /> },
        { key: 'enter', labelKey: 'pages.setup.geofences.enterShort', render: (form) => <YesNo value={form.alertOnEnter} /> },
        { key: 'exit', labelKey: 'pages.setup.geofences.exitShort', render: (form) => <YesNo value={form.alertOnExit} /> },
        { key: 'noShow', labelKey: 'pages.setup.geofences.noShowEnabled', render: (form) => <NoShowSummary form={form} /> },
      ]}
    />
  );
};

// The table mounts once the senior's time zone is known so the adapter (which converts the
// no-show time with it) is built a single time.
const Geofences = () => {
  const { t } = useTranslation();
  const timezone = useSeniorTimezone();

  if (!timezone) return <p className='text-sm text-muted-foreground'>{t('common.loading')}</p>;

  return <GeofencesTable timezone={timezone} />;
};

export default Geofences;
